import api from '../api';
import {
    buildCompanyOverview,
    getCompanyOverviewMetrics,
    exportCompanyOverviewXls,
    mergeVideotecaIntoChecklist,
    normalizeVideotecaStatus,
} from './companyOverview';

function authHeaders(accessToken) {
    return { Authorization: `Bearer ${accessToken}` };
}

function jsonHeaders(accessToken) {
    return {
        ...authHeaders(accessToken),
        'Content-Type': 'application/json',
    };
}

function buildReportParams(filters = {}) {
    const params = {};
    if (filters.typeSystem) params.typeSystem = String(filters.typeSystem);
    if (filters.typeBusiness) params.typeBusiness = String(filters.typeBusiness);
    const search = (filters.search || '').trim();
    if (search) params.search = search;
    if (filters.period) params.period = String(filters.period);
    return params;
}

export default {
    async createCompany(accessToken, payload) {
        try {
            const response = await api.post(`/admin/companies`, payload, {
                headers: jsonHeaders(accessToken),
            });
            return response.data;
        } catch (error) {
            console.error('Erro ao criar company:', error);
            console.error('create company status:', error?.response?.status);
            console.error('create company response:', error?.response?.data);
            throw error;
        }
    },
    async createCompanyById(accessToken, id) {
        try {
            const response = await api.get(`/admin/companies/${id}`, {
                headers: authHeaders(accessToken),
            });
            return response.data;
        } catch (error) {
            console.error('Erro ao carregar company:', error);
            throw error;
        }
    },
    getCompanyById(accessToken, id) {
        return this.createCompanyById(accessToken, id);
    },
    async updateCompany(accessToken, companyId, payload) {
        try {
            const response = await api.put(`/admin/companies/${companyId}`, payload, {
                headers: jsonHeaders(accessToken),
            });
            return response.data;
        } catch (error) {
            console.error('Erro ao atualizar company:', error);
            throw error;
        }
    },
    /** LEGADO: PUT /admin/company/{id} */
    async updateCompanyLegacy(accessToken, companyId, payload) {
        const response = await api.put(`/admin/company/${companyId}`, payload, {
            headers: jsonHeaders(accessToken),
        });
        return response.data;
    },
    async editLayoutCompany(accessToken, payload) {
        try {
            const companyId = payload?.idCompany;
            if (!companyId) {
                throw new Error('idCompany é obrigatório para salvar o layout.');
            }
            const response = await api.put(`/admin/companies/${companyId}/site`, payload, {
                headers: jsonHeaders(accessToken),
            });
            return response.data;
        } catch (error) {
            console.error('Erro ao editar layout da company:', error);
            throw error;
        }
    },
    /**
     * Listagem de usuários do super admin.
     * GET /admin/users
     */
    async listSuperAdminUsers(accessToken, { page = 0, size = 10, search = '', status } = {}) {
        const params = { page, size };
        const term = String(search || '').trim();
        if (term) params.search = term;
        if (status !== '' && status != null) params.status = status;
        const response = await api.get('/admin/users', {
            headers: authHeaders(accessToken),
            params,
        });
        return response.data;
    },
    /** POST /admin/users */
    async createSuperAdminUser(accessToken, payload) {
        const response = await api.post('/admin/users', { ...payload, type: 1 }, {
            headers: jsonHeaders(accessToken),
        });
        return response.data;
    },
    /** PUT /admin/users/{id} */
    async updateSuperAdminUser(accessToken, id, payload) {
        const response = await api.put(`/admin/users/${id}`, { ...payload, type: 1 }, {
            headers: jsonHeaders(accessToken),
        });
        return response.data;
    },
    /** Preferir GET /admin/companies (substitui o legacy). */
    async listCompany(accessToken) {
        try {
            const response = await api.get(`/admin/companies`, {
                headers: authHeaders(accessToken),
            });
            return response.data;
        } catch (error) {
            console.error('Erro ao listar companies:', error);
            throw error;
        }
    },
    /** LEGADO: GET /admin/company */
    async listCompanyLegacy(accessToken) {
        const response = await api.get(`/admin/company`, {
            headers: authHeaders(accessToken),
        });
        return response.data;
    },
    async approveSale(accessToken, companyId) {
        try {
            const response = await api.put(
                `/admin/companies/${companyId}/approve-sale`,
                {},
                { headers: jsonHeaders(accessToken) },
            );
            return response.data;
        } catch (error) {
            console.error('Erro ao aprovar venda:', error);
            throw error;
        }
    },
    /** LEGADO: PUT /admin/company/{id}/approve-sale */
    async approveSaleLegacy(accessToken, companyId) {
        const response = await api.put(
            `/admin/company/${companyId}/approve-sale`,
            { cartStatus: 1 },
            { headers: jsonHeaders(accessToken) },
        );
        return response.data;
    },
    /**
     * Atualiza recebedor Pagar.me; cria se ainda não houver código.
     * Body opcional: SuperAdminCompanyCreateRequestDTO { company, representative }.
     */
    async updatePagarmeRecipient(accessToken, companyId, payload = {}) {
        const response = await api.put(
            `/admin/companies/${companyId}/pagarme/recipient`,
            payload,
            { headers: jsonHeaders(accessToken) },
        );
        return response.data;
    },
    /** Recria recebedor Pagar.me (POST). */
    async retryPagarmeRecipient(accessToken, companyId) {
        const response = await api.post(
            `/admin/companies/${companyId}/pagarme/recipient`,
            {},
            { headers: authHeaders(accessToken) },
        );
        return response.data;
    },
    async createVideotecaFolder(accessToken, companyId) {
        const response = await api.post(
            `/admin/companies/${companyId}/videoteca/folder`,
            {},
            { headers: authHeaders(accessToken) },
        );
        return response.data;
    },
    async getVideoteca(accessToken, companyId) {
        const response = await api.get(`/admin/companies/${companyId}/videoteca`, {
            headers: authHeaders(accessToken),
        });
        return response.data;
    },
    async linkVideotecaAccount(accessToken, companyId, payload) {
        const response = await api.put(
            `/admin/companies/${companyId}/videoteca/account`,
            payload,
            { headers: jsonHeaders(accessToken) },
        );
        return response.data;
    },
    async getDashboardMetrics(accessToken) {
        const response = await api.get('/admin/dashboard', {
            headers: authHeaders(accessToken),
        });
        return response.data;
    },
    async getReportsData(accessToken, filters = {}) {
        const response = await api.get('/admin/reports', {
            headers: authHeaders(accessToken),
            params: buildReportParams(filters),
        });
        return response.data;
    },
    async getReportClients(accessToken, filters = {}) {
        const response = await api.get('/admin/reports/clients', {
            headers: authHeaders(accessToken),
            params: buildReportParams(filters),
        });
        return response.data;
    },
    async getReportFinancial(accessToken, filters = {}) {
        const response = await api.get('/admin/reports/financial', {
            headers: authHeaders(accessToken),
            params: buildReportParams(filters),
        });
        return response.data;
    },
    async getReportCoursesSales(accessToken, filters = {}) {
        const response = await api.get('/admin/reports/courses-sales', {
            headers: authHeaders(accessToken),
            params: buildReportParams(filters),
        });
        return response.data;
    },
    /**
     * Andamento do cliente (KPIs + checklist + charts).
     * GET /admin/companies/{id}/overview
     */
    async getCompanyOverview(accessToken, companyId) {
        const response = await api.get(`/admin/companies/${companyId}/overview`, {
            headers: authHeaders(accessToken),
        });
        return response.data;
    },
    getCompanyOverviewMetrics,
    buildCompanyOverview,
    exportCompanyOverviewXls,
    mergeVideotecaIntoChecklist,
    normalizeVideotecaStatus,
};
